import { useCallback } from 'react';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useMarkToolbarButton, useMarkToolbarButtonState } from '@platejs/utils/react';
import { useIndentButton, useOutdentButton } from '@platejs/indent/react';
import { toggleList, someList } from '@platejs/list';
import { BULLETED_STYLES, NUMBERED_STYLES } from '../listStyles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import styles from './PlateToolbar.module.scss';
import { BlockTypeMenu } from '../BlockTypeMenu/BlockTypeMenu';
import { TablePickerMenu } from '../TablePickerMenu/TablePickerMenu';
import { FontFamilyMenu } from '../FontFamilyMenu/FontFamilyMenu';
import { FontSizeMenu } from '../FontSizeMenu/FontSizeMenu';
import { TextColorMenu } from '../TextColorMenu/TextColorMenu';
import { HighlightColorMenu } from '../HighlightColorMenu/HighlightColorMenu';
import { LinkMenu } from '../LinkMenu/LinkMenu';
import { AlignMenu } from '../AlignMenu/AlignMenu';
import { LineHeightMenu } from '../LineHeightMenu/LineHeightMenu';
import { CellBackgroundMenu } from '../CellBackgroundMenu/CellBackgroundMenu';
import { ImageButton } from '../ImageButton/ImageButton';
import { ImportDocxButton } from '../ImportDocxButton/ImportDocxButton';
import { ColumnInsertMenu } from '../ColumnInsertMenu/ColumnInsertMenu';
import { AIToolbarButton } from '../AIToolbarButton/AIToolbarButton';
import { FindReplaceButton } from '../FindReplaceButton/FindReplaceButton';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';

interface MarkButtonProps {
    nodeType: string;
    label: string;
    children: React.ReactNode;
}

// Single library-driven button used for every inline mark (bold / italic /
// underline / strikethrough / code). useMarkToolbarButtonState reads the
// active mark off the editor, useMarkToolbarButton returns the onClick +
// onMouseDown handlers that toggle the mark while preserving focus.
function MarkButton({ nodeType, label, children }: MarkButtonProps) {
    const state = (useMarkToolbarButtonState as any)({ nodeType });
    const { props: btnProps } = (useMarkToolbarButton as any)(state);
    return (
        <button
            type="button"
            {...btnProps}
            className={clsx(styles.btn, state.pressed && styles.btnActive)}
            title={label}
            aria-label={label}
            aria-pressed={state.pressed || undefined}
        >
            {children}
        </button>
    );
}

// Kept for buttons that don't slot into a library hook — outdent/indent are
// already wired to @platejs/indent/react's hooks below.
interface ToolbarButtonProps {
    onMouseDown: (e: React.MouseEvent) => void;
    active?: boolean;
    label: string;
    children: React.ReactNode;
}

function ToolbarButton({ onMouseDown, active, label, children }: ToolbarButtonProps) {
    return (
        <button
            type="button"
            className={clsx(styles.btn, active && styles.btnActive)}
            onMouseDown={onMouseDown}
            title={label}
            aria-label={label}
            aria-pressed={active || undefined}
        >
            {children}
        </button>
    );
}

interface PlateToolbarProps {
    variant?: 'minimal' | 'slim' | 'full';
    /** Suppress specific mark buttons (the plugins themselves stay loaded). */
    hiddenMarks?: Array<'code' | 'subscript' | 'superscript'>;
}

export function PlateToolbar({ variant = 'full', hiddenMarks = [] }: PlateToolbarProps) {
    // In `minimal` mode, strip the toolbar down to bold/italic/underline/
    // strikethrough + text-colour + bg-colour + bulleted/numbered lists.
    // Code/sub/sup are forced off regardless of `hiddenMarks`.
    const isMinimal = variant === 'minimal';
    const effectiveHidden: Array<'code' | 'subscript' | 'superscript'> = isMinimal
        ? Array.from(new Set<'code' | 'subscript' | 'superscript'>([...hiddenMarks, 'code', 'subscript', 'superscript']))
        : hiddenMarks;
    const hide = (m: 'code' | 'subscript' | 'superscript') => effectiveHidden.includes(m);
    const editor = useEditorRef();

    // Lists still need a manual useEditorSelector — Plate doesn't ship a
    // matching `useListToolbarButton` in our installed packages (only in the
    // unshipped registry components). Same for indent/outdent (those use
    // @platejs/indent/react's own hooks below).
    const isBulletedList = useEditorSelector((ed: any) => (someList as any)(ed, BULLETED_STYLES), []);
    const isNumberedList = useEditorSelector((ed: any) => (someList as any)(ed, NUMBERED_STYLES), []);

    const { props: indentProps } = (useIndentButton as any)();
    const { props: outdentProps } = (useOutdentButton as any)();
    // `useSelectionFragmentProp` (used by AlignMenu) returns undefined the
    // moment selected blocks DISAGREE on a prop — right for "uniform value or
    // nothing" reads like align, but wrong for indent: a nested list (indent
    // 1 + 2) or a mixed indented/plain selection disagrees by design, and
    // Outdent should still be enabled since at least one block can outdent.
    // Read the MAX indent across the selected blocks instead — same
    // `useEditorSelector` pattern as the list-active checks above, scoped to
    // the current selection (`ed.api.blocks()` defaults `at` to
    // `editor.selection`), not the whole doc.
    const maxIndentInSelection = useEditorSelector(
        (ed: any) => (ed.api.blocks?.() ?? []).reduce((max: number, [node]: [any, unknown]) => Math.max(max, Number(node?.indent) || 0), 0),
        [],
    );
    const hasIndent = maxIndentInSelection > 0;

    const runList = useCallback(
        (listStyleType: string) => (e: React.MouseEvent) => {
            e.preventDefault();
            (toggleList as any)(editor, { listStyleType });
        },
        [editor],
    );

    const insertTable = useCallback(
        (rowCount: number, colCount: number) => {
            (editor as any).tf?.insert?.table?.({ rowCount, colCount });
        },
        [editor],
    );

    // Generic helpers for plugin-block inserts that don't have a dedicated
    // transform exposed by Plate. They all funnel through Slate's
    // `insertNodes` so the caret + history behave consistently.
    const insertHr = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            try {
                const ed: any = editor;
                ed?.tf?.insertNodes?.({ type: 'hr', children: [{ text: '' }] });
            } catch {
                /* defensive — hr plugin must be registered */
            }
        },
        [editor],
    );
    const isFull = variant === 'full';
    // Gate the wrapping group + divider on `editor.AI.enabled`. The
    // AIToolbarButton itself also null-returns when disabled (defense in
    // depth), but checking here too avoids a stray empty `.group` div and
    // dangling divider on the left of the toolbar when AI is off.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });
    const showAI = !isMinimal && aiEnabled.write;

    return (
        <div className={clsx(styles.toolbar, isMinimal && styles.toolbarMinimal)} role="toolbar" aria-label="Editor toolbar">
            {/* AI assistant — leftmost slot, renders in `full` and `slim`
                variants (not `minimal`) when `editor.AI.enabled` is on.
                Dispatches a window event the PlateEditor listens for;
                PlateEditor's listener is independently gated by the same flag. */}
            {showAI && (
                <>
                    <div className={styles.group}>
                        <AIToolbarButton />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}
            {isFull && (
                <>
                    <div className={styles.group}>
                        <BlockTypeMenu />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            {isFull && (
                <>
                    <div className={styles.group}>
                        <FontFamilyMenu />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            {/* Font size is available in EVERY variant, including `minimal` (the
                comments editor), like LinkMenu below — FontSizePlugin is always
                registered and plateToHtml serializes the `fontSize` mark, so sized
                comment text round-trips. FontFamilyMenu stays `isFull`-only. */}
            <div className={styles.group}>
                <FontSizeMenu />
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                <MarkButton nodeType="bold" label="Bold (⌘/Ctrl+B)">
                    <FormatBoldIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="italic" label="Italic (⌘/Ctrl+I)">
                    <FormatItalicIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="underline" label="Underline (⌘/Ctrl+U)">
                    <FormatUnderlinedIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="strikethrough" label="Strikethrough">
                    <FormatStrikethroughIcon fontSize="small" />
                </MarkButton>
                {!hide('code') && (
                    <MarkButton nodeType="code" label="Inline code">
                        <CodeIcon fontSize="small" />
                    </MarkButton>
                )}
                {!hide('subscript') && (
                    <MarkButton nodeType="subscript" label="Subscript">
                        <SubscriptIcon fontSize="small" />
                    </MarkButton>
                )}
                {!hide('superscript') && (
                    <MarkButton nodeType="superscript" label="Superscript">
                        <SuperscriptIcon fontSize="small" />
                    </MarkButton>
                )}
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                <TextColorMenu />
                <HighlightColorMenu />
                {isFull && <CellBackgroundMenu />}
            </div>
            <div className={styles.divider} aria-hidden />
            {!isMinimal && (
                <>
                    <div className={styles.group}>
                        <AlignMenu />
                        {isFull && <LineHeightMenu />}
                        {/* Kept adjacent to AlignMenu — indent-shifted text is fixed here,
                            not via Align-Left, which only sets textAlign. */}
                        {isFull && (
                            <>
                                <button
                                    type="button"
                                    className={clsx(styles.btn, hasIndent && styles.btnActive)}
                                    {...outdentProps}
                                    disabled={!hasIndent}
                                    title="Outdent"
                                    aria-label="Outdent"
                                    aria-pressed={hasIndent || undefined}
                                >
                                    <FormatIndentDecreaseIcon fontSize="small" />
                                </button>
                                <button type="button" className={styles.btn} {...indentProps} title="Indent" aria-label="Indent">
                                    <FormatIndentIncreaseIcon fontSize="small" />
                                </button>
                            </>
                        )}
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            <div className={styles.group}>
                <ToolbarButton onMouseDown={runList('disc')} active={isBulletedList} label="Bulleted list">
                    <FormatListBulletedIcon fontSize="small" />
                </ToolbarButton>
                <ToolbarButton onMouseDown={runList('decimal')} active={isNumberedList} label="Numbered list">
                    <FormatListNumberedIcon fontSize="small" />
                </ToolbarButton>
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                {/* Link is available in EVERY variant, including `minimal` (the
                    comments editor) — RECUR-26761 AC2. Image/table/divider/
                    import/find stay gated so minimal gains only the link button. */}
                <LinkMenu />
                {isFull && <TablePickerMenu onSelect={insertTable} />}
                {!isMinimal && <ImageButton />}
                {isFull && <ColumnInsertMenu />}
                {isFull && (
                    <ToolbarButton onMouseDown={insertHr} label="Divider (horizontal line)">
                        <HorizontalRuleIcon fontSize="small" />
                    </ToolbarButton>
                )}
                {/* Import a Word .docx / Markdown file → Plate content (full toolbar only). */}
                {isFull && <ImportDocxButton />}
                {/* Find & replace — full toolbar only. Dispatches a document
                    event that PlateEditor forwards to FindReplacePanel's ref. */}
                {isFull && <FindReplaceButton />}
            </div>
        </div>
    );
}
